import { useEffect, useRef, useState } from 'react';

/**
 * ESTADO QUE VIVE EN LA URL: un `useState` que sobrevive a su componente.
 *
 * QUE PROBLEMA RESUELVE. Buscar «dragapult» en el catálogo de la comunidad,
 * abrir uno de los mazos que salen y volver: la tabla aparecía en blanco, sin
 * búsqueda, sin filtros y en la página 1. No es un olvido de nadie sino el
 * ciclo de vida: entrar al detalle DESMONTA la lista (MazosView cambia de
 * modo), y con ella se va todo su `useState`. Pasa igual al cambiar de
 * pestaña, porque App remonta la vista entera con una `key` nueva. Volver a
 * dejar la pantalla como estaba exige guardar ese estado FUERA del componente.
 *
 * Y EL SITIO DE LA CASA PARA ESO ES LA BARRA DE DIRECCIONES. Es donde ya viven
 * la búsqueda, los filtros y la página de la enciclopedia (ver el reflejo de
 * App.jsx), así que lo que se recupera al volver es exactamente lo mismo que
 * se recupera al recargar o al abrir el enlace en otra pestaña: una sola
 * mecánica para los tres casos. Un `sessionStorage` habría arreglado la ida y
 * vuelta dejando fuera las otras dos, y encima con un estado invisible que
 * nadie puede compartir ni corregir a mano.
 *
 * REGLAS DE LA MECANICA, y cada una tiene su porqué:
 *
 * - CADA GANCHO TOCA SOLO SU CLAVE. La query se lee entera y se reescribe
 *   cambiando una sola clave, así que conviven los filtros de la enciclopedia
 *   (App), los del catálogo y los del meta sin pisarse. Como `replaceState` es
 *   síncrono, dos ganchos que cambian en el mismo ciclo se ven el uno al otro.
 * - SE REEMPLAZA, NUNCA SE APILA. Filtrar no es navegar: apilar una entrada
 *   por tecla convertiría «atrás» en deshacer la búsqueda letra a letra. Es la
 *   misma decisión que toma App para los filtros de cartas.
 * - EL `history.state` SE CONSERVA. Ahí guarda `useVolverCierra` la
 *   profundidad de las capas abiertas (`modal`); un `replaceState` con estado
 *   vacío la borraría y el «atrás» de un modal aterrizaría en cualquier sitio.
 * - EL VALOR POR DEFECTO NO SE ESCRIBE. Lo que no se ha tocado no ensucia la
 *   URL: la clave solo aparece cuando su valor se aparta del de arranque.
 * - LO QUE LLEGA DE FUERA SE VALIDA. Una URL se edita a mano y se comparte
 *   recortada: un `orden=; DROP` o un `tipo=loquesea` vuelven al valor por
 *   defecto en vez de pintar una tabla que nadie pidió. Es la misma regla que
 *   el resto de la casa aplica a los datos que no controla.
 *
 * NO se relee en `popstate` a propósito: quien vuelve con «atrás» a una vista
 * la está remontando (la capa se cierra, o App cambia de vista), y el gancho
 * lee la URL al montar. Escuchar además el historial duplicaría la restauración
 * y pelearía con la de App, que ya la hace para lo suyo.
 */

/** El texto de la URL, interpretado con el tipo del valor de arranque. */
function interpretar(cru, inicial) {
  if (typeof inicial === 'boolean') return cru === '1';
  if (typeof inicial === 'number') {
    const n = Number(cru);
    return Number.isFinite(n) ? n : inicial;
  }
  return cru;
}

/** Como viaja un valor en la URL; `null` = la clave no aparece. */
function comoTexto(valor) {
  if (valor == null || valor === '') return null;
  if (typeof valor === 'boolean') return valor ? '1' : null;
  return String(valor);
}

/** El filtro de lo aceptable: una lista blanca, una función, o todo vale. */
function aceptador(valores) {
  if (typeof valores === 'function') return valores;
  if (Array.isArray(valores)) return (v) => valores.includes(v);
  return () => true;
}

function leerDeUrl(clave, inicial, acepta) {
  let cru = null;
  try {
    cru = new URLSearchParams(window.location.search).get(clave);
  } catch {
    return inicial;
  }
  if (cru == null || cru === '') return inicial;
  const v = interpretar(cru, inicial);
  return acepta(v) ? v : inicial;
}

/**
 * `useState` con la URL como memoria.
 *
 * @param {string} clave      Nombre del parámetro. Que no choque con los de
 *                            App (`q`, `p`, `sub`, `legal` y los filtros de
 *                            cartas), que se escriben desde cualquier vista.
 * @param {*} inicial         Valor de arranque; su TIPO decide cómo se lee el
 *                            texto de la URL (booleano, número o cadena) y su
 *                            valor es el que no se escribe.
 * @param {object} opciones   `valores`: lista blanca o predicado de validación.
 */
export function useEstadoUrl(clave, inicial, opciones = {}) {
  const [valor, setValor] = useState(() =>
    leerDeUrl(clave, inicial, aceptador(opciones.valores))
  );

  /*
   * El valor por defecto se fija en el PRIMER render y no se recalcula: hay
   * llamadas que arrancan de una prop (el mazo con el que aterriza «ver
   * partidas»), y si el defecto cambiara con ella, la clave entraría y saldría
   * de la URL sola.
   */
  const porDefecto = useRef(inicial);

  useEffect(() => {
    let p;
    try {
      p = new URLSearchParams(window.location.search);
    } catch {
      return;
    }
    const texto = Object.is(valor, porDefecto.current) ? null : comoTexto(valor);
    if (texto == null) p.delete(clave);
    else p.set(clave, texto);

    const q = p.toString();
    if (q === window.location.search.replace(/^\?/, '')) return;
    const url = window.location.pathname + (q ? `?${q}` : '') + window.location.hash;
    window.history.replaceState(window.history.state ?? {}, '', url);
  }, [clave, valor]);

  return [valor, setValor];
}
